import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Chip,
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material';
import { SftpDestinationDialog, type DestinationFormData } from './SftpDestinationDialog';
import { SftpSqlColumnMapDialog } from './SftpSqlColumnMapDialog';

export interface SqlColumn {
  columnName: string;
  type: string;
  csvHeader: string;
}

export interface Destination {
  destinationId: string;
  type: string;
  connectionString?: string;
  tableName: string;
  schemaName: string;
  sqlBeforeScript?: string;
  sqlAfterScript?: string;
  scriptTimeout: string;
  isEnabled: boolean;
  sqlColumns?: SqlColumn[];
}

interface SftpDestinationsListProps {
  destinations: Destination[];
  configId: string;
  onAddDestination: (destination: Destination) => void;
  onUpdateDestination: (destination: Destination) => void;
  onRemoveDestination: (destinationId: string) => void;
  onToggleDestination: (destinationId: string) => void;
  onUpdateSqlColumns: (destinationId: string, columns: SqlColumn[]) => void;
  isLoading?: boolean;
}

export const SftpDestinationsList: React.FC<SftpDestinationsListProps> = ({
  destinations,
  configId: _configId, // marked as unused since it's not currently used in this component
  onAddDestination,
  onUpdateDestination,
  onRemoveDestination,
  onToggleDestination,
  onUpdateSqlColumns,
  isLoading = false,
}) => {
  const [destinationDialogOpen, setDestinationDialogOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<DestinationFormData | undefined>();
  const [destinationMode, setDestinationMode] = useState<'create' | 'edit'>('create');
  const [sqlColumnsDialogOpen, setSqlColumnsDialogOpen] = useState(false);
  const [selectedDestinationForColumns, setSelectedDestinationForColumns] = useState<Destination | undefined>();
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [destToRemove, setDestToRemove] = useState<Destination | undefined>();

  const handleAddDestination = () => {
    setSelectedDestination(undefined);
    setDestinationMode('create');
    setDestinationDialogOpen(true);
  };

  const handleEditDestination = (destination: Destination) => {
    // Convert destination to DestinationFormData format
    const formData = {
      ...destination,
      connectionString: '',
      sqlBeforeScript: destination.sqlBeforeScript || '',
      sqlAfterScript: destination.sqlAfterScript || '',
    };
    setSelectedDestination(formData);
    setDestinationMode('edit');
    setDestinationDialogOpen(true);
  };

  const handleSaveDestination = (destination: Destination) => {
    if (destinationMode === 'create') {
      onAddDestination(destination);
    } else {
      onUpdateDestination(destination);
    }
    setDestinationDialogOpen(false);
  };

  const handleEditSqlColumns = (destination: Destination) => {
    setSelectedDestinationForColumns(destination);
    setSqlColumnsDialogOpen(true);
  };

  const handleSaveSqlColumns = (columns: SqlColumn[]) => {
    if (selectedDestinationForColumns) {
      onUpdateSqlColumns(selectedDestinationForColumns.destinationId, columns);
    }
    setSqlColumnsDialogOpen(false);
  };

  const handleRemoveClick = (destination: Destination) => {
    setDestToRemove(destination);
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = () => {
    if (destToRemove) {
      onRemoveDestination(destToRemove.destinationId);
    }
    setRemoveConfirmOpen(false);
    setDestToRemove(undefined);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Destinations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDestination}
          disabled={isLoading}
        >
          Add Destination
        </Button>
      </Box>

      {destinations.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No destinations configured. Click "Add Destination" to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {destinations.map((destination) => (
            <Grid item xs={12} key={destination.destinationId}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {destination.schemaName}.{destination.tableName}
                        </Typography>
                        <Chip
                          label={destination.isEnabled ? 'Enabled' : 'Disabled'}
                          color={destination.isEnabled ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip
                          label={destination.type}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      {destination.sqlColumns && destination.sqlColumns.length > 0 && (
                        <Typography variant="body2" color="textSecondary" mt={1}>
                          {destination.sqlColumns.length} column mapping(s) configured
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Configure SQL Columns">
                        <IconButton
                          onClick={() => handleEditSqlColumns(destination)}
                          disabled={isLoading}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={destination.isEnabled ? 'Disable' : 'Enable'}>
                        <IconButton
                          onClick={() => onToggleDestination(destination.destinationId)}
                          disabled={isLoading}
                          color={destination.isEnabled ? 'success' : 'default'}
                        >
                          <PowerIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEditDestination(destination)}
                          disabled={isLoading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={() => handleRemoveClick(destination)}
                          disabled={isLoading}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SftpDestinationDialog
        open={destinationDialogOpen}
        onClose={() => setDestinationDialogOpen(false)}
        onSave={handleSaveDestination}
        destination={selectedDestination}
        mode={destinationMode}
      />

      {selectedDestinationForColumns && (
        <SftpSqlColumnMapDialog
          open={sqlColumnsDialogOpen}
          onClose={() => setSqlColumnsDialogOpen(false)}
          onSave={handleSaveSqlColumns}
          columns={selectedDestinationForColumns.sqlColumns || []}
          destinationName={`${selectedDestinationForColumns.schemaName}.${selectedDestinationForColumns.tableName}`}
        />
      )}

      <Dialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
      >
        <DialogTitle>Remove Destination</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the destination "{destToRemove?.schemaName}.{destToRemove?.tableName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};